/**
 * Activity-feed kind → icon-bubble tint + Material Symbols glyph.
 *
 * Mirrors the design handoff's `AUDIT_TINT` map (handoff `mui_conversion/ui.tsx`)
 * and the prototype's per-entry glyphs, keyed by the four feed kinds the spec
 * defines (`purchase | credit | membership | reservation`) plus a neutral fallback
 * for an unrecognized/absent kind (the parser yields `null` there — see
 * `api/audit.ts`).
 *
 * MODE-AWARE since DNKADM-0269: the tint/ink pairs live in
 * `src/theme/maps/activity.ts` (this feature dir is literal-free under the 0269
 * ESLint guard). Callers pass `theme.palette.mode`; the default keeps light —
 * and every pre-0269 call site/test — byte-identical.
 */
import type { ActivityKind } from '@/api/audit';
import { buildActivityKindMaps } from '@/theme/maps/activity';

export interface KindStyle {
  /** Icon-bubble background tint. */
  bg: string;
  /** Icon-bubble foreground (glyph) color. */
  fg: string;
  /** Material Symbols Outlined glyph name. */
  icon: string;
}

/** Resolve the bubble tint + glyph for a feed entry's kind (null → neutral). */
export function styleForKind(
  kind: ActivityKind | null,
  mode: 'light' | 'dark' = 'light',
): KindStyle {
  const maps = buildActivityKindMaps(mode);
  return kind !== null ? maps[kind] : maps.fallback;
}
